import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import DateTimePicker from '../../components/DateTimePicker';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect, { SingleSelectOption } from '../../components/SingleSelect';
import LcResolutionBoard, { STATUS_OPTIONS } from '../../components/scheduling/LcResolutionBoard';
import { useAdditionalFieldSelection } from '../../hooks/useAdditionalFields';
import { useToast } from '../../contexts/ToastContext';
import schedulingService from '../../services/scheduling.service';
import ordersService from '../../services/orders.service';
import userSettingsService from '../../services/userSettings.service';
import { matchLcToCurrentUser } from '../../utils/matchLcToCurrentUser';
import { buildScopeSubtitle } from './ttsServiceFailuresScope';
import { LcResolutionItem } from '../../types/scheduling';

/** YYYY-MM-DD in local time (the format the report endpoint expects). */
const formatDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ALL_STATUS_VALUES = STATUS_OPTIONS.map((o) => o.value);

/** Per-user settings key for the persisted LC + Region filters on this page. */
const FILTERS_KEY = 'tts_service_failures_filters';

/** Which date the range filters on. Values match the report endpoint's date_basis. */
const DATE_BASIS_OPTIONS: SingleSelectOption[] = [
  { value: 'created', label: 'Created' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'servicefailure', label: 'Service Failure' },
];

/**
 * Researching/Reports variant of the TTs & Service Failures page. Pulls ALL
 * trouble tickets + service failures within a date range (default last 7 days),
 * scoped by LC (no default — shows everyone) and Region (order -> dispatcher ->
 * region), filtered to the selected ticket statuses (default Resolved). Editing
 * + saving behave the same as the operational page via the shared board; the
 * driver-ticket red highlight is intentionally operational-only.
 */
// One key for the layout AND the added-column selection — they belong to the same
// grid, and the board takes it as a prop.
const SETTINGS_KEY = 'reports-tts-and-service-failures-v2';

// The filters as the page opens. Defined once so Reset and the initial state
// cannot drift — the bug otherwise is a Reset that quietly lands somewhere the
// page never opens on.
const DEFAULT_DATE_BASIS = 'created';
const DEFAULT_STATUS_FILTER = ['RESOLVED'];
/** Last 7 days. Recomputed per call, so a Reset lands on today rather than on
 *  whatever today was when the tab was first opened. */
const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start, end };
};

export default function TTsServiceFailuresReport() {
  const fieldSelection = useAdditionalFieldSelection(SETTINGS_KEY);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { showToast } = useToast();
  const { accounts } = useMsal();

  // ── Filters ──────────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState<Date | null>(() => defaultDateRange().start);
  const [endDate, setEndDate] = useState<Date | null>(() => defaultDateRange().end);
  // create date (TT or SF) in range
  const [dateBasis, setDateBasis] = useState<string>(DEFAULT_DATE_BASIS);
  const [lcFilter, setLcFilter] = useState<string[]>([]); // resolved below: saved, else current user
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([...DEFAULT_STATUS_FILTER]);

  const [lcOptions, setLcOptions] = useState<MultiSelectOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);

  // Load filter options once.
  useEffect(() => {
    let cancelled = false;
    schedulingService
      .getFilterOptions()
      .then((res) => {
        if (cancelled) return;
        setLcOptions(res.logistics_coordinators.map((lc) => ({ value: lc.value, label: lc.label })));
      })
      .catch(() => { /* leave empty */ });
    ordersService
      .getRegions()
      .then((res) => {
        if (cancelled) return;
        setRegionOptions(res.data.regions.map((r) => ({ value: r, label: r })));
      })
      .catch(() => { /* leave empty */ });
    return () => { cancelled = true; };
  }, []);

  // Persist the LC + Region filters per user so they survive a refresh or a
  // return to the page (users were re-selecting them every time, especially
  // after a deploy). Dates intentionally aren't persisted — the default is a
  // rolling last-7-days window. Mirrors the LC Dashboard's filter persistence.
  const filtersLoaded = useRef(false);
  const saveFiltersTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether the user already has a saved (non-empty) LC selection — if so it
  // wins over the current-user default below.
  const savedLcApplied = useRef(false);
  const lcDefaultApplied = useRef(false);
  const [settingsResolved, setSettingsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    userSettingsService
      .getUserSetting(FILTERS_KEY)
      .then((setting) => {
        if (cancelled) return;
        const saved = setting?.setting_value;
        if (saved) {
          if (Array.isArray(saved.lcFilter) && saved.lcFilter.length > 0) {
            setLcFilter(saved.lcFilter);
            savedLcApplied.current = true;
          }
          if (Array.isArray(saved.regionFilter)) setRegionFilter(saved.regionFilter);
        }
      })
      .catch(() => { /* no saved filters yet */ })
      .finally(() => {
        filtersLoaded.current = true;
        if (!cancelled) setSettingsResolved(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Default the LC filter to the current user (matched by full name, like the
  // Scheduling grid) once both the LC options and saved settings have resolved,
  // but only when the user has no saved LC selection of their own — an explicit
  // choice always wins.
  useEffect(() => {
    if (lcDefaultApplied.current) return;
    if (!settingsResolved || !lcOptions.length) return;
    lcDefaultApplied.current = true;
    if (savedLcApplied.current) return;
    const match = matchLcToCurrentUser(lcOptions, accounts[0]);
    if (match) setLcFilter([match.value]);
  }, [settingsResolved, lcOptions, accounts]);

  useEffect(() => {
    // Don't save until the initial load has run, or the empty default state
    // would overwrite the user's saved filters before they're restored.
    if (!filtersLoaded.current) return;
    if (saveFiltersTimeoutRef.current) clearTimeout(saveFiltersTimeoutRef.current);
    saveFiltersTimeoutRef.current = setTimeout(() => {
      userSettingsService
        .saveUserSetting(FILTERS_KEY, { lcFilter, regionFilter })
        .catch((err) => console.error('Failed to save TTs report filters:', err));
    }, 500);
    return () => {
      if (saveFiltersTimeoutRef.current) clearTimeout(saveFiltersTimeoutRef.current);
    };
  }, [lcFilter, regionFilter]);

  /** Put every filter back to how the page opens.
   *
   * LC goes back to the CURRENT USER, not to All LCs: the current user is what
   * the page defaults to, and clearing it would quietly widen the report to the
   * whole company instead of returning it to its opening scope. LC and Region
   * are persisted per user, so the reset is saved like any other change.
   */
  const resetFilters = useCallback(() => {
    const { start, end } = defaultDateRange();
    setStartDate(start);
    setEndDate(end);
    setDateBasis(DEFAULT_DATE_BASIS);
    setRegionFilter([]);
    setStatusFilter([...DEFAULT_STATUS_FILTER]);
    const match = matchLcToCurrentUser(lcOptions, accounts[0]);
    setLcFilter(match ? [match.value] : []);
  }, [lcOptions, accounts]);

  // ── Row fetch ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<LcResolutionItem[]>([]);
  // Starts loading: the first fetch waits on the saved column selection, and
  // until it lands the grid must show a spinner, not "No data available".
  const [loading, setLoading] = useState(true);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const fetchRows = useCallback(async (isBackgroundRefresh = false) => {
    if (!startDate || !endDate) return;

    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    if (!isBackgroundRefresh) setLoading(true);
    try {
      const list = await schedulingService.getLcResolutionReport(
        {
          startDate: formatDateKey(startDate),
          endDate: formatDateKey(endDate),
          csrs: lcFilter,
          regions: regionFilter,
          // Empty selection means "all statuses"; default state is Resolved.
          statuses: statusFilter.length ? statusFilter : ALL_STATUS_VALUES,
          dateBasis,
          additionalFields: fieldSelection.additionalFields,
        },
        controller.signal,
      );
      setRows(list);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch TTs & service failures report:', err);
      if (!isBackgroundRefresh) {
        showToast({ type: 'error', message: 'Failed to load TTs & service failures report' });
      }
    } finally {
      // Only the request still in flight owns the spinner. An aborted fetch
      // (superseded by a filter change) must not switch it off while its
      // replacement is still loading — that left the grid saying "No data
      // available" mid-load.
      if (!isBackgroundRefresh && fetchAbortRef.current === controller) setLoading(false);
    }
  }, [startDate, endDate, dateBasis, lcFilter, regionFilter, statusFilter, showToast, additionalFieldsKey]);

  // The saved selection is part of the request, so hold the first fetch for it.
  useEffect(() => {
    if (!fieldSelection.ready) return;
    void fetchRows();
  }, [fetchRows, fieldSelection.ready]);
  useEffect(() => () => fetchAbortRef.current?.abort(), []);

  // Spell the loaded scope out under the title. Every filter refetches on
  // change, so this always describes the rows on screen — and it puts the
  // Status default (Resolved only) somewhere it can't be read past before
  // someone exports the month and reconciles it against V1.
  const scopeSubtitle = useMemo(
    () =>
      `${buildScopeSubtitle({
        dateBasisLabel: DATE_BASIS_OPTIONS.find((o) => o.value === dateBasis)?.label ?? 'Created',
        startDate,
        endDate,
        statuses: statusFilter,
        statusOptions: STATUS_OPTIONS,
        lcs: lcFilter,
        regions: regionFilter,
      })}. Edit inline; right-click + drag a column to bulk edit.`,
    [dateBasis, startDate, endDate, statusFilter, lcFilter, regionFilter],
  );

  return (
    <LcResolutionBoard
      rows={rows}
      loading={loading}
      onRefetch={(opts) => { void fetchRows(opts?.background ?? false); }}
      title="TTs & Service Failures Report"
      subtitle={scopeSubtitle}
      // -v2: bumped to drop stale persisted sort (old troubleticketrecno default)
      // so the chronological backend order becomes the default. See LcResolutionBoard.
      settingsKey={SETTINGS_KEY}
      fieldSelection={fieldSelection}
      // The report's scope is the filter bar above it, so a pasted list is reconciled
      // against whatever date range, LCs, regions and statuses are currently set — which
      // is easy to forget once a list of pros is on screen.
      listFilterScopeNote={
        'This report holds only the tickets and service failures matching the date range, '
        + 'LCs, regions and statuses set above. A pro outside those filters will read as '
        + 'unmatched — widen the filters before concluding it has none.'
      }
      // No auto-refresh + no driver-ticket red highlight on the report page.
      highlightDriverTicketY={false}
      showResolveColumn
      // Reviewed checkbox + Reviewed By / Date/Time: a manager's pass over coded
      // work, so it lives here and not on the LC queue.
      showReviewColumns
      filterBar={
        <>
          <div className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
            <SingleSelect
              options={DATE_BASIS_OPTIONS}
              value={dateBasis}
              onChange={setDateBasis}
              size="sm"
              className="min-w-[150px]"
            />
            <span>between</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={startDate}
              onChange={(val) => setStartDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="Start"
              autoApply
            />
            <span>and</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={endDate}
              onChange={(val) => setEndDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="End"
              autoApply
            />
          </div>

          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">LC</label>
          <MultiSelect
            options={lcOptions}
            value={lcFilter}
            onChange={setLcFilter}
            placeholder="All LCs"
            showClearOption
            clearOptionLabel="All LCs"
            enableSearch
            searchPlaceholder="Search LCs..."
            size="sm"
            className="min-w-[200px]"
          />

          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Region</label>
          <MultiSelect
            options={regionOptions}
            value={regionFilter}
            onChange={setRegionFilter}
            placeholder="All Regions"
            showClearOption
            clearOptionLabel="All Regions"
            enableSearch
            searchPlaceholder="Search regions..."
            size="sm"
            className="min-w-[180px]"
          />

          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
          <MultiSelect
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All Statuses"
            showClearOption
            clearOptionLabel="All Statuses"
            size="sm"
            className="min-w-[160px]"
          />

          <button
            type="button"
            onClick={resetFilters}
            className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400 whitespace-nowrap"
          >
            Reset filters
          </button>
        </>
      }
    />
  );
}
